import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';

interface PrivacyDoc {
  content: string;
  updatedAt: string;
}

/**
 * Página pública con la política de privacidad. Accesible sin login en
 * `/legal/privacidad`. Cumple el requisito de "información accesible al
 * interesado" del Art. 13 RGPD: cualquier persona (incluso antes de
 * loguearse o tras dejar la empresa) puede consultar el texto vigente.
 */
export function PrivacyPolicyPublic() {
  const [doc, setDoc] = useState<PrivacyDoc | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<PrivacyDoc>('/legal/privacy')
      .then((d) => {
        if (!cancelled) setDoc(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error cargando la política');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <header className="border-b border-border bg-paper">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-icaria-600" />
            <h1 className="text-base font-semibold tracking-tightish">Política de privacidad</h1>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/login">
              <ArrowLeft className="h-3.5 w-3.5" />
              Volver al login
            </Link>
          </Button>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-8">
          {error ? (
            <p className="text-sm text-destructive">No se pudo cargar la política: {error}</p>
          ) : !doc ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <article className="prose prose-sm max-w-none text-[14px] leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.content}</ReactMarkdown>
              <p className="text-[12px] text-muted-foreground mt-8">
                Última actualización del documento: {new Date(doc.updatedAt).toLocaleDateString('es-ES')}
              </p>
            </article>
          )}
        </div>
      </main>
    </div>
  );
}
